/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import * as d3 from 'd3';
import {
    AfterViewInit,
    ApplicationRef,
    Component,
    ComponentRef,
    createComponent,
    Input,
    OnDestroy,
    ViewChild
} from '@angular/core';
import { ChartNodeComponent } from './node/chart-node.component';
import { ChartNodeModel } from '../../models';
import { SearchRowsComponent } from '../searchRows/search-rows.component';
import { UtilsService } from '../../services';

@Component({
    selector: 'app-chart',
    templateUrl: './chart.component.html',
    styleUrls: ['./chart.component.scss']
})
export class ChartComponent implements AfterViewInit, OnDestroy {
    /**
     * Reference to the search rows component to clear rows.
     */
    @ViewChild(SearchRowsComponent) child: SearchRowsComponent;

    /**
     * Incoming data from parent component.
     * @private
     */
    @Input() set data(data: ChartNodeModel[]) {
        if (this.searchData.length) {
            this.child.clearRows();
        }

        this.searchData = [];
        this.svg.select('#path-wrapper').remove();
        this.svg.select('#foreign-wrapper').remove();
        this._data = data;
        const formattedData = this.getFormattedData(data);
        this.searchData = formattedData.nodes.map((item: ChartNodeModel) => item.details);
        this.addDataToChart(formattedData);
    }

    /**
     * Store the search data.
     */
    public searchData: any = [];

    /**
     * Store the search keys.
     */
    public searchKeys: string[] = ['status', 'label', 'subnet', 'gateway'];

    /**
     * Store the SVG in a container.
     * @private
     */
    private svg: d3.Selection<SVGSVGElement, unknown, HTMLElement, any>;

    /**
     * Group of elements that zoom.
     * @private
     */
    private zoomGroup: d3.Selection<SVGGElement, unknown, HTMLElement, any>;

    /**
     * Zoom transition animation time.
     * @private
     */
    private zoomTransitionTime: number = 750;

    /**
     * Throttle timeout ID.
     * @private
     */
    private throttleTimeout = null;

    /**
     * Store the zoom variable from reference to reset and search
     * @private
     */
    private zoom: d3.ZoomBehavior<Element, unknown>;

    /**
     * Width of the SVG
     * @private
     */
    private width: number = 960;

    /**
     * Height of the SVG
     * @private
     */
    private height: number = 200;

    /**
     * Declare the gap between nodes.
     * @private
     */
    private gap: number = 20;

    /**
     * Store the nodes for lookup with search.
     * @private
     */
    private nodes: any[] = [];

    /**
     * Store the incoming data from the parent component.
     * @private
     */
    private _data: any[] = [];

    /**
     * Zoom type/
     * @private
     */
    private zoomToUse: (...args: any[]) => void = this.fitZoom;

    constructor(
        private appRef: ApplicationRef,
        private utilsService: UtilsService,
    ) {
    }

    get data() {
        return this._data;
    }

    ngAfterViewInit(): void {
        this.createD3Container();
    }

    ngOnDestroy(): void {
        clearTimeout(this.throttleTimeout);
    }

    /**
     * Search the nodes object.
     * @param $event
     */
    public search($event: any): void {
        if ($event.length === this.nodes.length) {
            this.zoomToUse();
        } else {
            const node = this.nodes.find((item: ChartNodeModel) => item.details === $event[0]);
            if (node) {
                const scale: number = 1.5; // Zoom scale
                const transform = d3.zoomIdentity
                    .translate(this.width / 2 - node.x * scale, this.height / 2 - node.y * scale)
                    .scale(scale);

                this.svg.transition()
                    .duration(this.zoomTransitionTime)
                    .call(this.zoom.transform, transform);
            }
        }
    }

    /**
     * Reset zoom helper function for button in view.
     */
    public fitZoom(): void {
        this.zoomToUse = this.fitZoom;
        // Calculate the bounding box of all nodes
        const nodes = this.nodes;
        if (!nodes.length) {
            return;
        }

        const minX = d3.min(nodes, (d: any) => d.x - d.width / 2);
        const maxX = d3.max(nodes, (d: any) => d.x + d.width / 2);
        const minY = d3.min(nodes, (d: any) => d.y - d.height / 2);
        const maxY = d3.max(nodes, (d: any) => d.y + d.height / 2);

        // Determine the width and height of the bounding box
        const boundingBoxWidth = (maxX - minX) + this.gap;
        const boundingBoxHeight = (maxY - minY) + this.gap;

        // Determine the scaling factor to fit all nodes within the SVG viewBox
        const scale = Math.min(this.width / boundingBoxWidth, this.height / boundingBoxHeight);

        // Compute the translation to center the nodes in the SVG viewBox
        const translateX = ((this.width - boundingBoxWidth * scale) / 2 - minX * scale) + (this.gap / 4);
        const translateY = ((this.height - boundingBoxHeight * scale) / 2 - minY * scale) + (this.gap / 1.5);

        // Apply the transformation
        const transform = d3.zoomIdentity
            .translate(translateX, translateY)
            .scale(scale);

        this.svg.transition()
            .duration(this.zoomTransitionTime)
            .call(this.zoom.transform, transform);
    }

    /**
     * Create the D3 container.
     * @private
     */
    private createD3Container(): any {
        this.svg = d3.select('#d3-container')
            .append('svg')
            .attr('viewBox', `0 0 ${this.width} ${this.height}`)
            .attr('class', 'chart');

        this.zoomGroup = this.svg.append('g')
            .attr('transform', 'translate(0,0)');

        this.zoom = d3.zoom()
            .scaleExtent([0.1, 4])
            .on('zoom', (event: any) => this.onZoom(event));

        this.svg.call(this.zoom);
    }

    /**
     * On Zoom.
     * @param event
     * @private
     */
    private onZoom(event: any): void {
        if (this.throttleTimeout) {
            return;
        }
        this.throttleTimeout = setTimeout(() => {
            this.zoomGroup.attr('transform', event.transform);
            this.throttleTimeout = null;
        }, 10);
    }

    /**
     * Setup page.
     * @private
     */
    private getFormattedData(data: any[]): any {
        const nodes = this.setupNodes(data);
        this.nodes = nodes;
        const { links, linkPath } = this.setupLinks(nodes);

        return { nodes, links, linkPath };
    }

    /**
     * Set up nodes
     * @param data
     * @private
     */
    private setupNodes(data: any[]): any[] {
        // Declare setup variables
        const gap = this.gap;
        let nodes: any[] = [];
        let xStartPosition: number;
        let yStartPosition: number;
        let totalWidth: number;

        // Separate the data into parents and children
        const parentNodes: any[] = data.filter(item => item.type === 'parent');
        const childNodes: any[] = data.filter(item => item.type === 'child');

        // Find the tallest node in the parents array.
        const parentMaxHeight = parentNodes.length ? Math.max(...parentNodes.map((item: any) => item.height)) : 0;
        // Find the tallest node in the child array.
        const childMaxHeight = childNodes.length ? Math.max(...childNodes.map((item: any) => item.height)) : 0;
        // Get the total width of all the parent nodes plus gaps (but minus the last gap)
        totalWidth = parentNodes.reduce((a, b) => a + b.width + gap, 0) - gap;
        // Get the total height of all the parent and child nodes.
        const totalHeight: number = parentMaxHeight + childMaxHeight;

        // Set the y position based on the child nodes length
        yStartPosition = this.getParentYStartPosition(childNodes, totalHeight, gap, parentMaxHeight);

        // Find the middle of the svg
        xStartPosition = (this.width - totalWidth) / 2;

        // Format parent nodes.
        nodes = nodes.concat(this.formatNodes(xStartPosition, yStartPosition, parentNodes, gap));

        // Get the total width including the gap, minus the gap on the last node.
        totalWidth = childNodes.reduce((a, b) => a + b.width + gap, 0) - gap;

        // Set the y position based on the parent nodes length
        yStartPosition = this.getChildYStartPosition(parentNodes, totalHeight, gap, yStartPosition);

        // Set the x start position
        xStartPosition = (this.width - totalWidth) / 2;

        // Format the child nodes
        nodes = nodes.concat(this.formatNodes(xStartPosition, yStartPosition, childNodes, gap));

        return nodes;
    }

    /**
     * Set size and position of each node.
     * @param xStartPosition
     * @param yStartPosition
     * @param items
     * @param gap
     * @private
     */
    private formatNodes(xStartPosition: number, yStartPosition: number, items: any, gap: number): any {
        const nodes: any[] = [];

        for (const item of items) {
            const y: number = yStartPosition;
            const x: number = xStartPosition + (item.width / 2);

            nodes.push(
                {
                    id: item.label,
                    class: item.class,
                    details: item.details,
                    type: item.type,
                    x,
                    y,
                    height: item.height,
                    width: item.width
                }
            );

            xStartPosition = xStartPosition + item.width + gap;
        }

        return nodes;
    }

    /**
     * Get Y start position for parents.
     * @param childNodes
     * @param totalHeight
     * @param gap
     * @param parentMaxHeight
     * @private
     */
    private getParentYStartPosition(childNodes: any[], totalHeight: number, gap: number, parentMaxHeight: number): number {
        return childNodes.length
            ? ((this.height - (totalHeight + (gap * 2))) / 2) + (parentMaxHeight / 2)
            : this.height / 2;
    }

    /**
     * Get Y start position for children
     * @param parentNodes
     * @param totalHeight
     * @param gap
     * @param yStartPosition
     * @private
     */
    private getChildYStartPosition(parentNodes: any[], totalHeight: number, gap: number, yStartPosition: number): number {
        return parentNodes.length
            ? yStartPosition + (totalHeight / 2) + (gap * 2)
            : this.height / 2;
    }

    /**
     * Set up link between nodes.
     * @param nodes
     * @private
     */
    private setupLinks(nodes: any[]): any {
        const childNodes: any[] = nodes.filter(item => item.type === 'child');
        const links = childNodes.map(child => ({
            source: nodes[0],
            target: child,
            class: child.details.status
        }));

        const childHeight = Math.max.apply(null, childNodes.map((item: any) => item.height));

        const linkPath = (item: {
            source: { y: number; x: any; height: number; };
            target: { y: number; x: any; height: number; };
        }): string => {
            const midY: number = item.target.y - (childHeight / 2) - this.gap;

            return `M ${item.source.x} ${item.source.y + item.source.height / 2}
                        L ${item.source.x} ${midY}
                        L ${item.target.x} ${midY}
                        L ${item.target.x} ${item.target.y - item.target.height / 2}`;
        };

        return { links, linkPath };
    }

    /**
     * Add data to chart.
     * @param data
     * @private
     */
    private addDataToChart(data: { links: any; linkPath: any; nodes: any; }): void {
        // Sort the links so that the offline path(s) is/are drawn last, this means they sit on top of the online one
        // allowing the colour to show.
        const links = this.utilsService.sortArrayByProperty(data.links, 'class', ['online', 'offline']);
        this.zoomGroup.append('g')
            .attr('id', 'path-wrapper')
            .selectAll('path')
            .data(links)
            .join('path')
            .attr('d', data.linkPath)
            // Style the paths here to cover ViewEncapsulation issues.
            .attr('stroke', (data: any) => data['class'] === 'online' ? '#00b391' : '#ff00a3')
            .attr('fill', 'none');

        const foreignNodes = this.zoomGroup
            .append('g')
            .attr('id', 'foreign-wrapper')
            .selectAll()
            .data(data.nodes)
            .enter()
            .append('foreignObject')
            .attr('x', (node: any) => node.x - node.width / 2)
            .attr('y', (node: any) => node.y - node.height / 2)
            .attr('width', (node: any) => node.width)
            .attr('height', (node: any) => node.height);

        foreignNodes.each((data: any, index: number, nodes: any) => {
            this.embedAngularComponent(nodes[index], data);
        });

        this.zoomToUse();
    }

    /**
     * Embed angular component.
     * @param element
     * @param data
     * @private
     */
    private embedAngularComponent(element: any, data: any): void {
        const component = ChartNodeComponent;
        const componentRef: ComponentRef<any> = createComponent(component, {
            environmentInjector: this.appRef.injector,
        });
        componentRef.instance.data = data;
        const domElem = (componentRef.hostView as any).rootNodes[0] as HTMLElement;
        element.appendChild(domElem);
        componentRef.changeDetectorRef.detectChanges();
    }
}
